import { CalendarIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns/format";
import { getYear } from "date-fns/getYear";
import { subDays } from "date-fns/subDays";
import React from "react";
import { isMobile } from "react-device-detect";
import { toast } from "react-hot-toast";

import { ErrorToast, StepType } from "../components";
import {
  SelectDetailsStep,
  SelectMovementTypeStep,
  SelectPlayerStep,
  UploadFilesStep,
} from "../features";
import { VisualizerSessionInfo } from "../features/DataVisualizer/DataVisualizer";
import { useAppSelector } from "../redux/hooks";
import {
  FormTypeEnum,
  Org,
  Player,
  PlayerFormValues,
  UploadSessionFormValues,
} from "../types";

type UploadSessionStepListProps = {
  /** List of players, used in the first screen for the player select */
  playerList: Player[];
  /** The form values for the session upload process */
  sessionFormValues: UploadSessionFormValues;
  /** Organization data used in the upload session form process */
  orgData: Org;
};

export const possibleMovementTypes: { [key: string]: string } = {
  baseballPitching: "Pitches",
  baseballHitting: "Swings",
  basketballShooting: "Shots",
  verticalJumping: "Jumps",
  squatting: "Squats",
  lunging: "Lunges",
  legRaising: "Leg Raises",
  cervicalFlexion: "Cervical Flex/Ext",
  cervicalRotation: "Cervical Rotation",
  countermovementJumping: "Countermovement Jump",
  multisegFlexion: "Multi-Segment Flex/Ext",
  multisegRotation: "Multi-Segment Rotation",
  shoulderPatterns: "Shoulder Patterns",
  shoulderRom: "Shoulder ROM",
  singlelegStance: "Single Leg Stance",
};

export const CustomCalendarIcon = (): JSX.Element => {
  return (
    <CalendarIcon
      className="h-6 pr-2 text-gray-700 stroke-2"
      data-testid="calendar-icon"
    />
  );
};

// Sourced from this Stack Overflow thread: https://stackoverflow.com/questions/3009993/what-would-be-regex-for-matching-foreign-characters
export const regexForForeignLettersAndSpecialChars = /^[\p{L}'\-\s\.]*$/u;

export const currentYear = format(new Date(), "yyyy");
export const today = format(new Date(), "M/d/yyyy");
export const years = Array.from(
  { length: getYear(new Date()) + 1 - 1990 },
  (_, i) => 1990 + i,
);

export const dateRangeOptions = [
  { value: today, label: "Today" },
  { value: format(subDays(new Date(), 1), "M/d/yyyy"), label: "Yesterday" },
  {
    value: `${format(subDays(new Date(), 7), "M/d/yyyy")} - ${today}`,
    label: "Last 7 Days",
  },
  {
    value: `${format(subDays(new Date(), 14), "M/d/yyyy")} - ${today}`,
    label: "Last 14 Days",
  },
  {
    value: `${format(subDays(new Date(), 30), "M/d/yyyy")} - ${today}`,
    label: "Last 30 Days",
  },
  {
    value: `${format(subDays(new Date(), 60), "M/d/yyyy")} - ${today}`,
    label: "Last 60 Days",
  },
  {
    value: `${format(subDays(new Date(), 365), "M/d/yyyy")} - ${today}`,
    label: "Last Year",
  },
  { value: "Custom Range", label: "Custom Range" },
  { value: "Individual Dates", label: "Individual Dates" },
];

export const playerListLimit = 2000;
export const sessionListLimit = 1000;

/** Returns the message shown in tables when a fetch fails */
export const normalizeFetchError = (): string =>
  "Fetching failed, please refresh to try again.";

/** Returns true for 5xx responses or RTK Query network failures (API unreachable). */
export const isCriticalFetchError = (err: unknown): boolean => {
  if (typeof err !== "object" || err === null || !("status" in err)) {
    return false;
  }
  const status = (err as { status: unknown }).status;
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") {
    return true;
  }
  return typeof status === "number" && status >= 500 && status < 600;
};

/** True when the primary org query fails with a server/network error. */
export const hasDashboardCriticalError = (
  orgInfoError: boolean,
  orgError: unknown,
): boolean => orgInfoError && isCriticalFetchError(orgError);

export const emptyPlayer: Player = {
  id: "",
  firstName: "",
  middleName: "",
  lastName: "",
  dateOfBirth: "",
  throws: "",
  hits: "",
  height: {
    feet: "",
    inches: "",
  },
  weight: "",
  orgPlayerId: "",
  officialLeagueId: "",
};

export const emptyPlayerFormValues: PlayerFormValues = {
  firstName: "",
  middleName: "",
  lastName: "",
  dateOfBirth: null,
  throws: { value: "", label: "" },
  hits: { value: "", label: "" },
  heightFeet: "",
  heightInches: "",
  weight: "",
  orgPlayerId: "",
  officialLeagueId: "",
};

export const emptyProgressModalState = {
  shouldOpen: false,
  title: "",
};

export const emptyModalState = {
  shouldOpen: false,
  title: "",
  steps: [],
  singleStep: undefined,
  currentStep: 1,
};

export const emptyDialogState = {
  shouldOpen: false,
  type: "",
};

export const emptyVisualizerInfo: VisualizerSessionInfo = {
  playerName: "",
  orgPlayerId: "",
  domHand: "",
  sessionId: "",
  sessionDate: "",
};

export const uploadSessionStepList = ({
  playerList,
  sessionFormValues,
  orgData,
}: UploadSessionStepListProps): StepType[] => {
  const validateFrameRate = (frameRate: string): boolean => {
    if (frameRate === "") {
      return true;
    }

    const frameRateNumber = parseInt(frameRate, 10);
    return frameRateNumber >= 28 && frameRateNumber <= 1002;
  };

  return [
    {
      title: isMobile ? "Player" : "Select Player",
      display: <SelectPlayerStep players={playerList ? playerList : []} />,
      disableNext: sessionFormValues.selectedPlayer.value === "",
      formType: FormTypeEnum.UploadSessionForm,
    },
    {
      title: "Details",
      display: <SelectDetailsStep orgData={orgData} />,
      disableNext:
        sessionFormValues.sessionType.value === "" ||
        sessionFormValues.sessionVenue.value === "",
      formType: FormTypeEnum.UploadSessionForm,
    },
    {
      title: isMobile ? "Movement" : "Select Movement Type",
      display: <SelectMovementTypeStep orgData={orgData} />,
      disableNext:
        sessionFormValues.movementType.value === "" ||
        sessionFormValues.frameRate === "" ||
        !validateFrameRate(sessionFormValues.frameRate),
      formType: FormTypeEnum.UploadSessionForm,
    },
    {
      title: "Upload",
      display: <UploadFilesStep />,
      disableNext: sessionFormValues.files.length === 0,
      formType: FormTypeEnum.UploadSessionForm,
    },
  ];
};

export const errorToast = (message: string, id?: string): string =>
  toast.custom(<ErrorToast message={message} classNames={"mt-modal"} />, {
    id: id ? id : "",
  });

export const customSelectStyles = {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  control: (provided: any) => ({
    ...provided,
    fontSize: "0.875rem",
    borderColor: "#DBDEE2",
    borderRadius: "6px",
  }),
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  option: (styles: any, { isFocused, isSelected }: any) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? "#F9FAFB"
        : isFocused
          ? "#F9FAFB"
          : undefined,
      color: isSelected ? "#FB6A07" : undefined,
      ":active": {
        ...styles[":active"],
        backgroundColor: isSelected ? "#F9FAFB" : undefined,
      },
    };
  },
};

export const useSessionsTableFilters = () => {
  return [
    {
      type: "date",
      placeholder: "Session Date",
      filterOptions: dateRangeOptions,
      columnId: "sessionDate",
    },
    {
      type: "dropdown",
      placeholder: "Session Type",
      filterOptions: useSessionTypeOptions(),
      columnId: "typeAndVenue",
    },
    {
      type: "dropdown",
      placeholder: "Mocap Type",
      filterOptions: useMocapTypeOptions(),
      columnId: "sessionIdColumn",
    },
  ];
};

export const useReportsTableFilters = (dateColumnId = "createdAt") => {
  const reportsTableFilters = [
    {
      type: "date",
      placeholder: "Date Range",
      filterOptions: dateRangeOptions,
      columnId: dateColumnId,
    },
    {
      type: "dropdown",
      placeholder: "Movement Type",
      filterOptions: useMovementTypeOptions(),
      columnId: "movementType",
    },
  ];
  return reportsTableFilters;
};

export const useMovementTypeOptions = () => {
  const orgData = useAppSelector((state) => state.org);
  const movementTypes = orgData.details.movementTypes
    .filter((movement: any) => movement.slug !== "movement-screen")
    .map((movement: any) => {
      return { value: `${movement.name}`, label: `${movement.name}` };
    });

  return [...movementTypes, { value: "", label: "All Movements" }].sort(
    (a, b) => a.label.localeCompare(b.label),
  );
};

export const useSessionTypeOptions = () => {
  const orgData = useAppSelector((state) => state.org);

  const sessionTypes = orgData.details.sessionTypes
    ? orgData.details.sessionTypes.map((sessionType: any) => {
        const slug = sessionType.slug.includes("-")
          ? sessionType.slug.replace(/-/g, " ")
          : sessionType.slug;
        return { value: `${slug}`, label: `${sessionType.name}` };
      })
    : [];

  return [...sessionTypes, { value: "", label: "All Types" }].sort((a, b) =>
    a.label.localeCompare(b.label),
  );
};

const useMocapTypeOptions = () => {
  const orgData = useAppSelector((state) => state.org);
  const mocapTypes = orgData.details.mocapTypes.map((mocapType: any) => {
    return { value: `${mocapType.name}`, label: `${mocapType.name}` };
  });
  return [...mocapTypes, { value: "", label: "All Types" }].sort((a, b) =>
    a.label.localeCompare(b.label),
  );
};

export const movementScreenSlugs = [
  "shoulder-rom",
  "cervical-flexion",
  "multiseg-flexion",
  "squatting",
  "shoulder-patterns",
  "multiseg-rotation",
  "cervical-rotation",
  "singleleg-stance",
  "countermovement-jumping",
];

export const createGroupedMovementTypes = (movementTypes: any[]) => {
  const standardMovementTypes = movementTypes
    .filter(
      ({ slug }) => slug.includes("baseball") || slug.includes("basketball"),
    )
    .map(({ id, name, slug }) => ({ value: `${id}`, label: `${name}`, slug }));

  const generalPurposeMovementTypes = movementTypes
    .filter(
      ({ slug }) =>
        !slug.includes("baseball") &&
        !slug.includes("basketball") &&
        slug !== "movement-screen",
    )
    .map(({ id, name, slug }) => ({ value: `${id}`, label: `${name}`, slug }));

  const orderedGenPurposeMovementTypes = [
    ...generalPurposeMovementTypes
      .filter((type) => movementScreenSlugs.includes(type.slug))
      .sort(
        (a, b) =>
          movementScreenSlugs.indexOf(a.slug) -
          movementScreenSlugs.indexOf(b.slug),
      ),
    ...generalPurposeMovementTypes.filter(
      (type) => !movementScreenSlugs.includes(type.slug),
    ),
  ];
  generalPurposeMovementTypes.splice(
    0,
    generalPurposeMovementTypes.length,
    ...orderedGenPurposeMovementTypes,
  );

  return [
    {
      label: "",
      options: standardMovementTypes,
    },
    {
      label: "Functional Movements",
      options: generalPurposeMovementTypes,
    },
  ];
};
